import React, { useState, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import hpProducts from "../data/hp-products.json";
import epsonProducts from "../data/epson-product.json";
import brotherProducts from "../data/brother-products.json";
import canonProducts from "../data/canon-products.json";
import toast from "react-hot-toast";

const productData = [
  ...brotherProducts,
  ...canonProducts,
  ...hpProducts,
  ...epsonProducts,
];

function ShopDetailsMain() {
  const [activeTab, setActiveTab] = useState("tab-desc");
  const [quantity, setQuantity] = useState(1);

  const { id } = useParams();
  const navigate = useNavigate();

  const product = productData.find((p) => p.id === parseInt(id, 10));

  const randomReviews = useMemo(() => {
    const reviewers = [
      "James T.",
      "Olivia W.",
      "Michael C.",
      "Emma J.",
      "David M.",
    ];
    const comments = [
      "Excellent print quality and very easy to set up!",
      "Perfect for my home office. Quiet & reliable.",
      "Fast delivery and easy installation.",
      "Great value. Sharp prints every time.",
      "Wireless setup works smoothly. Highly recommended.",
    ];
    return Array.from({ length: 4 }, (_, i) => ({
      name: reviewers[i],
      text: comments[i],
      rating: Math.floor(Math.random() * 2) + 4,
      date: `January ${12 + i}, 2025`,
    }));
  }, []);

  const getImage = () => {
    if (!product) return "/assets/img/placeholder.png";
    if (Array.isArray(product.imageUrl) && product.imageUrl.length > 0)
      return product.imageUrl[0];
    if (typeof product.imageUrl === "string") return product.imageUrl;
    return "/assets/img/placeholder.png";
  };

  const handleAddToCart = () => {
    if (!product) return;

    const isLoggedIn = sessionStorage.getItem("isLoggedIn");
    if (isLoggedIn !== "true") {
      toast.error("Please login first!");
      navigate("/login-now");
      return;
    }

    let cart = JSON.parse(sessionStorage.getItem("cart")) || [];
    const existing = cart.find((item) => item.id === product.id);

    if (existing) existing.quantity += quantity;
    else cart.push({ ...product, quantity });

    sessionStorage.setItem("cart", JSON.stringify(cart));
    toast.success(`${product.name} added to cart`);
  };

  if (!product) {
    return <div className="text-center py-5">Product not found!</div>;
  }

  const avgRating = (Math.random() * 0.5 + 4.3).toFixed(1);
  const totalReviews = randomReviews.length + 20;

  return (
    <section className="product-details-wrapper">
      <div className="container">
        {/* PRODUCT GRID */}
        <div className="product-details-grid">
          {/* LEFT IMAGE BLOCK */}
          <div className="product-image-block">
            <img className="main-image" src={getImage()} alt={product.name} />
          </div>

          {/* RIGHT DETAILS BLOCK */}
          <div className="product-info-block">
            <h1 className="product-name">{product.name}</h1>

            <div className="rating-row">
              <span className="rating-stars">⭐ {avgRating}</span>
              <span className="rating-count">({totalReviews} reviews)</span>
            </div>

            <p className="product-description">{product.description}</p>

            <div className="price-box">
              <h2 className="price">${product.price}</h2>
              <span className="stock-label">✔ In Stock</span>
            </div>

            {/* QUANTITY & CART */}
            <div className="cart-row">
              <div className="qty-box">
                <button onClick={() => setQuantity((q) => Math.max(1, q - 1))}>
                  -
                </button>
                <span>{quantity}</span>
                <button onClick={() => setQuantity((q) => q + 1)}>+</button>
              </div>

              <button className="add-cart-btn" onClick={handleAddToCart}>
                Add to Cart
              </button>
            </div>

            {/* META */}
            <div className="meta-info">
              {product.brand && (
                <p>
                  <strong>Brand:</strong> {product.brand}
                </p>
              )}
              <p>
                <strong>Category:</strong>{" "}
                <Link to="/shop">{product.category.replace(/-/g, " ")}</Link>
              </p>
            </div>
          </div>
        </div>

        {/* TABS */}
        <div className="tabs-container">
          <div className="tab-buttons">
            <button
              className={activeTab === "tab-desc" ? "active" : ""}
              onClick={() => setActiveTab("tab-desc")}
            >
              Description
            </button>
            <button
              className={activeTab === "tab-additional" ? "active" : ""}
              onClick={() => setActiveTab("tab-additional")}
            >
              Additional Info
            </button>
            <button
              className={activeTab === "tab-reviews" ? "active" : ""}
              onClick={() => setActiveTab("tab-reviews")}
            >
              Reviews
            </button>
          </div>

          <div className="tab-content">
            {activeTab === "tab-desc" && (
              <p>{product.description || "No description available."}</p>
            )}

            {activeTab === "tab-additional" && (
              <p>{product.specifications || "Specifications coming soon."}</p>
            )}

            {activeTab === "tab-reviews" && (
              <div className="reviews-block">
                {randomReviews.map((r, i) => (
                  <div key={i} className="review-item">
                    <div className="review-avatar">{r.name.charAt(0)}</div>
                    <div>
                      <p className="review-name">{r.name}</p>
                      <p className="review-date">{r.date}</p>
                      <p className="review-stars">{"⭐".repeat(r.rating)}</p>
                      <p className="review-text">{r.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* STYLES */}
      <style>{`
        .product-details-wrapper {
          padding: 40px 0;
        }

        .product-details-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 50px;
          margin-bottom: 50px;
        }

        .product-image-block {
          padding: 25px;
          border-radius: 20px;
          background: #fff;
          box-shadow: 0px 8px 30px rgba(0,0,0,0.07);
        }

        .main-image {
          width: 100%;
          border-radius: 14px;
          transition: 0.3s;
        }
        .main-image:hover {
          transform: scale(1.03);
        }

        .product-info-block {
          padding-top: 10px;
        }

        .product-name {
          font-size: 32px;
          font-weight: 800;
          margin-bottom: 10px;
        }

        .rating-row {
          display: flex;
          gap: 8px;
          margin-bottom: 18px;
        }

        .rating-stars {
          background: #ffcc00;
          padding: 4px 10px;
          border-radius: 8px;
          font-weight: 600;
        }

        .product-description {
          color: #444;
          margin-bottom: 18px;
          line-height: 1.6;
        }

        .price-box {
          display: flex;
          align-items: center;
          gap: 14px;
          margin-bottom: 22px;
        }

        .price {
          font-size: 30px;
          font-weight: 800;
        }

        .stock-label {
          color: #0ca52a;
          font-weight: 700;
        }

        .cart-row {
          display: flex;
          gap: 18px;
          margin-bottom: 30px;
        }

        .qty-box {
          display: flex;
          align-items: center;
          background: #f1f1f1;
          border-radius: 10px;
        }

        .qty-box button {
          padding: 10px 14px;
          background: none;
          border: none;
          font-size: 18px;
          cursor: pointer;
        }

        .qty-box span {
          padding: 0 12px;
          font-size: 16px;
        }

        .add-cart-btn {
          background: #0b61d6;
          border: none;
          padding: 12px 24px;
          border-radius: 12px;
          color: white;
          font-size: 16px;
          font-weight: 700;
          cursor: pointer;
          transition: 0.3s;
        }
        .add-cart-btn:hover {
          background: #084ebb;
        }

        .meta-info p {
          margin-bottom: 6px;
          color: #555;
        }

        /* Tabs */
        .tabs-container {
          margin-top: 40px;
        }

        .tab-buttons {
          display: flex;
          gap: 10px;
          margin-bottom: 20px;
        }

        .tab-buttons button {
          padding: 10px 18px;
          border-radius: 10px;
          background: #eee;
          border: none;
          cursor: pointer;
          font-weight: 600;
        }

        .tab-buttons button.active {
          background: #0b61d6;
          color: white;
        }

        .tab-content {
          padding: 20px;
          background: white;
          border-radius: 14px;
          box-shadow: 0px 6px 20px rgba(0,0,0,0.06);
        }

        /* Reviews */
        .review-item {
          display: flex;
          gap: 15px;
          margin-bottom: 20px;
        }

        .review-avatar {
          width: 45px;
          height: 45px;
          background: #0b61d6;
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          font-size: 18px;
        }

        .review-name {
          font-weight: 700;
        }

        .review-text {
          margin-top: 5px;
          color: #444;
        }

        /* Responsive */
        @media (max-width: 900px) {
          .product-details-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </section>
  );
}

export default ShopDetailsMain;
